import { ArrowUpRight, Check } from "lucide-react";
import Layout, { PageHero } from "@/components/Layout";
import LeadForm from "@/components/LeadForm";
import { Reveal, Overline } from "@/components/Reveal";
import { useSettings } from "@/context/SettingsContext";

// Generic vertical landing page used by /capital /media /cre /aspire /private-network
export default function VerticalPage({
  overline, title, subtitle, image, statement, bullets = [],
  redirectKey, redirectLabel, forms = [], sourcePage, children,
}) {
  const { settings } = useSettings();
  const openExternal = () => {
    const url = settings.redirects?.[redirectKey];
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Layout>
      <PageHero overline={overline} title={title} subtitle={subtitle} image={image} />

      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            {statement && (
              <Reveal>
                <p className="font-display text-2xl font-bold leading-snug">{statement}</p>
              </Reveal>
            )}
            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-4">
                {bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-neutral-300">
                    <Check className="text-[#CC5500] shrink-0 mt-1" size={18} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            {redirectKey && (
              <Reveal delay={0.2}>
                <button onClick={openExternal} className="btn-ghost mt-10" data-testid="vertical-page-external-btn">
                  {redirectLabel} <ArrowUpRight size={16} />
                </button>
              </Reveal>
            )}
            {children}
          </div>

          <div className="space-y-12">
            {forms.map((fk) => (
              <Reveal key={fk} className="card-edge p-8">
                <LeadForm formKey={fk} sourcePage={sourcePage} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export function OpportunityList({ opportunities }) {
  if (!opportunities?.length) return null;
  return (
    <section className="py-16 max-w-7xl mx-auto px-6">
      <Overline>Opportunities</Overline>
      <h2 className="font-display text-3xl font-bold mt-3 mb-8">Current Opportunities</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {opportunities.map((o) => (
          <div key={o.opportunity_id} className="card-edge p-6" data-testid={`opp-${o.opportunity_id}`}>
            <div className="flex justify-between items-start">
              <span className="overline text-[#CC5500]">{o.type}</span>
              {o.visibility === "members" && <span className="text-xs text-[#D4AF37]">Members</span>}
            </div>
            <h3 className="font-display text-lg font-bold mt-3">{o.title}</h3>
            {o.location && <p className="text-neutral-500 text-sm mt-1">{o.location}</p>}
            <p className="text-neutral-400 text-sm mt-3">{o.short_description}</p>
            {o.price_range && <p className="text-white font-semibold mt-4">{o.price_range}</p>}
            {o.cta_link && (
              <a href={o.cta_link} target="_blank" rel="noopener noreferrer" className="text-[#CC5500] text-sm inline-flex items-center gap-1 mt-4 hover:underline">
                Learn more <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
